// Offline service for PWA functionality
export interface OfflineIdea {
  id?: number;
  content: string;
  timestamp: number;
  token: string;
}

class OfflineService {
  private dbName = 'BrainDumpOffline';
  private dbVersion = 1;
  private storeName = 'ideas';

  async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id', autoIncrement: true });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async saveOfflineIdea(content: string): Promise<void> {
    const token = localStorage.getItem('authToken') || '';
    const idea: OfflineIdea = {
      content,
      timestamp: Date.now(),
      token
    };

    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.add(idea);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve();
        // Register for background sync when connection is restored
        if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
          navigator.serviceWorker.ready.then((registration) => {
            // TypeScript doesn't recognize sync API, so we cast it
            (registration as any).sync.register('background-sync-ideas');
          });
        }
      };
    });
  }

  async getOfflineIdeas(): Promise<OfflineIdea[]> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore('ideas');
      const request = store.getAll();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  async clearOfflineIdeas(): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getOfflineCount(): Promise<number> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.count();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  onOnlineStatusChange(callback: (isOnline: boolean) => void): () => void {
    const handleOnline = () => callback(true);
    const handleOffline = () => callback(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Return cleanup function
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }

  // Alias methods for syncService compatibility
  async getPendingIdeas(): Promise<OfflineIdea[]> {
    return this.getOfflineIdeas();
  }

  async clearPendingIdeas(): Promise<void> {
    return this.clearOfflineIdeas();
  }
}

export const offlineService = new OfflineService();